import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

// Shared styling with LoginPage (institutional dark default, gold accent).
const INPUT_CLS = 'w-full rounded-lg px-3 py-2.5 lg:py-3 text-sm outline-none mb-3 '
  + 'bg-black/[0.03] border border-gray-300 text-gray-900 placeholder-gray-400 '
  + 'dark:bg-white/[0.06] dark:border-white/10 dark:text-white dark:placeholder-white/20';
const BTN_CLS = 'w-full py-3 lg:py-3.5 rounded-lg text-xs uppercase tracking-[3px] font-semibold '
  + 'cursor-pointer transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed '
  + 'bg-[#c9a96e] text-[#0a0a0a] hover:bg-[#dfc08a]';
const ERR_CLS = 'mb-4 px-4 py-3 rounded-lg text-sm bg-red-50 border border-red-200 text-red-600 '
  + 'dark:bg-red-900/20 dark:border-red-800/30 dark:text-red-400';

/**
 * Forced (or voluntary) password change. First-login users are routed here by PrivateRoute
 * while `profile.must_change_password` is true; on success the flag clears and they proceed.
 */
const ChangePasswordPage = () => {
  const { user, changePassword, logout } = useAuth();
  const navigate = useNavigate();
  const forced = !!user?.profile?.must_change_password;

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (next !== confirm) { setError('New passwords do not match.'); return; }
    if (next.length < 8) { setError('New password must be at least 8 characters.'); return; }
    setLoading(true);
    try {
      await changePassword(current, next);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not change password. Check your current password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gray-50 dark:bg-[#0a0a0a]">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="text-[10px] uppercase tracking-[4px] text-[#c9a96e] mb-2">Maghaweer.ai</div>
          <h1 className="font-serif text-2xl text-gray-900 dark:text-white">
            {forced ? 'Set your password' : 'Change password'}
          </h1>
          {forced && (
            <p className="mt-2 text-xs text-gray-500 dark:text-white/40 leading-relaxed">
              Your account was created with a temporary password. Choose a new one to continue.
            </p>
          )}
        </div>

        {error && <div className={ERR_CLS}>{error}</div>}

        <form onSubmit={submit}>
          <input type="password" className={INPUT_CLS} autoComplete="current-password"
                 placeholder={forced ? 'Temporary password' : 'Current password'}
                 value={current} onChange={(e) => setCurrent(e.target.value)} required />
          <input type="password" className={INPUT_CLS} autoComplete="new-password"
                 placeholder="New password" value={next}
                 onChange={(e) => setNext(e.target.value)} required />
          <input type="password" className={INPUT_CLS} autoComplete="new-password"
                 placeholder="Confirm new password" value={confirm}
                 onChange={(e) => setConfirm(e.target.value)} required />
          <button type="submit" className={BTN_CLS} disabled={loading}>
            {loading ? 'Saving…' : (forced ? 'Set password & continue' : 'Change password')}
          </button>
        </form>

        <button
          onClick={async () => { await logout(); navigate('/login', { replace: true }); }}
          className="mt-5 w-full text-center text-[11px] uppercase tracking-[2px] text-gray-400 dark:text-white/30 hover:text-[#c9a96e] transition-colors">
          Sign out
        </button>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
